// Shared dropdown-menu items for the TTS voice picker + rate stepper.
// Used by ChatContainerView and PracticePageView so their three-dot menus
// expose the same voice / rate controls beneath their Auto-Pronounce toggle.

import * as preact from "preact";

import * as modalController from "modalController";
import stores from "stores/stores";
import type { TTSFeature } from "tts";
import * as tts from "tts";
import type { MenuItem } from "views/DropdownMenu";
import TTSVoicePickerView from "views/TTSVoicePickerView";
import * as ttsUi from "views/ttsUi";

/**
 * Returns the [voice picker action, rate stepper] pair to append to a
 * dropdown menu when TTS is usable for (feature, language). Returns `[]`
 * when it is not so the menu doesn't advertise a picker with nothing in it.
 *
 * `onChange` is invoked after any write (voice pick committed, rate stepped,
 * picker closed) so the caller can re-render to show the new label / value.
 */
export const buildVoiceMenuItems = (opts: {
  language: string | undefined;
  feature: TTSFeature;
  onChange: () => void;
}): MenuItem[] => {
  const { language, feature, onChange } = opts;
  if (!language) return [];

  if (
    tts.getAvailabilitySignal({ user: stores.user, feature, language })
      .value !== "supported"
  ) {
    return [];
  }

  const pickerState = tts.getVoicePickerState({
    user: stores.user,
    feature,
    language,
  });
  const voiceDisplayName = pickerState.selectedVoiceName;
  const voiceLabel = voiceDisplayName
    ? `Voice: ${voiceDisplayName}…`
    : "Set voice…";

  const rate = tts.getRate({ user: stores.user, feature, language });
  const rateIndex = ttsUi.RATE_CHOICES.indexOf(rate);
  const effectiveRateIndex =
    rateIndex >= 0 ? rateIndex : ttsUi.RATE_CHOICES.indexOf(1);

  const stepRate = async (delta: number) => {
    const nextIndex = effectiveRateIndex + delta;
    if (nextIndex < 0 || nextIndex >= ttsUi.RATE_CHOICES.length) return;
    await tts.setRate({
      user: stores.user,
      feature,
      language,
      rate: ttsUi.RATE_CHOICES[nextIndex],
    });
    onChange();
  };

  const openPicker = () => {
    modalController.show(
      <TTSVoicePickerView
        language={language}
        feature={feature}
        close={() => {
          modalController.cancelAll();
          // Voice / rate writes may only touch localStorage, so force a
          // render here so the menu labels refresh.
          onChange();
        }}
      />
    );
  };

  return [
    { type: "action", label: voiceLabel, onClick: openPicker },
    {
      type: "stepper",
      label: "Rate:",
      value: `${ttsUi.RATE_CHOICES[effectiveRateIndex]}X`,
      onPrev: () => stepRate(-1),
      onNext: () => stepRate(1),
      prevDisabled: effectiveRateIndex <= 0,
      nextDisabled: effectiveRateIndex >= ttsUi.RATE_CHOICES.length - 1,
    },
  ];
};
